import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type PersonModel from 'tio-common/models/person';
import type EmployeeService from 'tio-employee/services/employee';
import type FeaturesService from 'tio-common/services/features';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type ReimaginedViewService from 'tio-employee/services/reimagined-view';
import type { ReimaginedLoanSummaryModel } from 'tio-employee/components/reimagined/production/loan-summary';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AccountActivityAccountsRouteModel = Resolved<
  ReturnType<AuthenticatedAccountActivityAccountsRoute['model']>
>;

export default class AuthenticatedAccountActivityAccountsRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare features: FeaturesService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare reimaginedView: ReimaginedViewService;

  async model() {
    // Refresh contribution data when navigating to this route (fixes DEV-4638)
    // This ensures contribution amounts display after loan upload without requiring a page refresh
    if (this.employee.contributionsState.isFinished) {
      this.employee.contributionsState.retry();
    }
    if (this.employee.futurePayments.isFinished) {
      this.employee.futurePayments.retry();
    }

    const personId = this.sessionContext.user.person.id;
    // @ts-expect-error type model
    const person = await this.store.findRecord<PersonModel>('person', personId, {
      include:
        'accounts.loans,accounts.financial-institution,accounts.postal-address,employees.plan',
      reload: true,
    });

    if (this.reimaginedView.isEnabled) {
      const [loansData, employerContributions, section127Info] = await Promise.all([
        this.reimaginedData.fetchLoansData(),
        this.reimaginedData.fetchEmployerContributions(),
        this.reimaginedData.fetchSection127Info(),
      ]);

      const employee = this.sessionContext.currentEmployee;
      const hasStrategyFinderResults = (employee?.strategyFinderResults?.length ?? 0) > 0;

      const reimagined: ReimaginedLoanSummaryModel = {
        ...loansData,
        employerContributions,
        section127Info,
        hasStrategyFinderResults,
      };

      return { person, reimagined };
    }

    return person;
  }
}
